import { z } from "zod";
import { CLAIM_STATUSES, JOB_STATUSES, MAX_PHOTO_BYTES, MAX_THUMB_BYTES, PHOTO_KINDS, ROLES, STATES, SYNC_TARGETS } from "./enums.js";
import { formConfigSchema } from "./form-config.js";
import { claimViewSchema, gbJobNumber } from "./job.js";

// Request/response shapes for every route. The web client and the API router both import these.

export const errorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorBody = z.infer<typeof errorBodySchema>;

export const listJobsQuerySchema = z.object({
  storeId: z.string().optional(),
  status: z.enum(JOB_STATUSES).optional(),
  claimStatus: z.enum(CLAIM_STATUSES).optional(),
  repId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListJobsQuery = z.infer<typeof listJobsQuerySchema>;

export const photoUploadRequestSchema = z.object({
  contentType: z.enum(["image/jpeg", "image/png", "image/heic", "image/webp"]),
  sizeBytes: z.number().int().positive().max(MAX_PHOTO_BYTES),
  kind: z.enum(PHOTO_KINDS),
  /** Size of the small JPEG preview the phone made, when it made one. */
  thumbSizeBytes: z.number().int().positive().max(MAX_THUMB_BYTES).optional(),
});
export const photoUploadResponseSchema = z.object({
  photoId: z.string(),
  uploadUrl: z.url(),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime(),
  /** Where the preview goes. Only when the request gave thumbSizeBytes. */
  thumb: z.object({ uploadUrl: z.url(), headers: z.record(z.string(), z.string()) }).optional(),
});

export const recordGlassBillerNumberSchema = z.object({
  glassBillerJobNumber: gbJobNumber,
});

export const vinDecodeResponseSchema = z.object({
  vin: z.string(),
  year: z.number().int().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  trim: z.string().optional(),
  adasLikely: z.boolean(),
});
export type VinDecodeResponse = z.infer<typeof vinDecodeResponseSchema>;

/** One row of the daily GlassBiller job export, after column mapping. */
export const glassBillerStatusRowSchema = z.object({
  glassBillerJobNumber: z.string().min(1),
  status: z.enum(["scheduled", "installed", "invoiced", "paid", "cancelled"]),
  scheduledFor: z.iso.datetime().optional(),
  installedAt: z.iso.datetime().optional(),
  expectedPaymentDate: z.iso.date().optional(),
});
export type GlassBillerStatusRow = z.infer<typeof glassBillerStatusRowSchema>;

// ---- Auth (passwordless: 6-digit code to email or phone, then an httpOnly session cookie) ----

export const requestCodeSchema = z.object({ login: z.string().trim().min(3).max(120) });
export const verifyCodeSchema = z.object({
  login: z.string().trim().min(3).max(120),
  code: z.string().regex(/^\d{6}$/),
});

export const storeSummarySchema = z.object({
  storeId: z.string(),
  name: z.string(),
  shop: z.string(),
  state: z.enum(STATES).optional(),
  formConfig: z.lazy(() => formConfigSchema),
});

export const meResponseSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  roles: z.array(z.enum(ROLES)),
  /** Dealerships this login works with: a rep's 2-3, a dealership's one, every active one for managers and admins. */
  stores: z.array(storeSummarySchema),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const retrySyncSchema = z.object({ target: z.enum(SYNC_TARGETS) });

export const listJobsResponseSchema = z.object({
  items: z.array(claimViewSchema),
  nextCursor: z.string().optional(),
});

/**
 * Route table. Everything sits under /api so CloudFront serves web and API from one origin
 * (no CORS, first-party cookies). Status codes: validation 422, duplicate_vin 409, conflict 409,
 * invalid_transition 409, forbidden 403, not_found 404, unauthenticated 401, too_large 413
 * (64 KB on JSON routes, 4 MB on the GlassBiller import), upstream 502.
 */
export const ROUTES = {
  requestCode: "POST /api/auth/code",
  verifyCode: "POST /api/auth/verify",
  logout: "POST /api/auth/logout",
  me: "GET /api/me",
  createPhotoUpload: "POST /api/photos/uploads",
  decodeVin: "GET /api/vin/:vin",
  submitJob: "POST /api/jobs",
  listJobs: "GET /api/jobs",
  getJob: "GET /api/jobs/:jobId",
  opsQueue: "GET /api/ops/queue",
  recordGlassBillerNumber: "POST /api/ops/jobs/:jobId/glassbiller-number",
  retrySync: "POST /api/ops/jobs/:jobId/retry-sync",
  importGlassBiller: "POST /api/ops/glassbiller-import",
  opsFailed: "GET /api/ops/failed",
  jobPhotos: "GET /api/jobs/:jobId/photos",
  updateClaim: "PATCH /api/ops/jobs/:jobId/claim",
  reports: "GET /api/reports",
  listPeople: "GET /api/admin/people",
  createPerson: "POST /api/admin/people",
  updatePerson: "PUT /api/admin/people/:userId",
  /** Admin: set a sales manager's reps in one call. Body { repIds }. Returns { items: Person[] }, the new team. */
  setTeam: "PUT /api/admin/people/:userId/team",
  /** Admin: change log for people and dealerships. Query entity, entityId, limit. Returns { items: AuditEntry[] }. */
  listAudit: "GET /api/admin/audit",
  /** GHL workflow webhook: status, GB number and schedule flowing back from GHL/GlassBiller. Shared-key auth. */
  ghlHook: "POST /api/hooks/ghl",
  /** GlassBiller rows from the BigQuery sync (`glassbiller-portal-push`): installs and schedules. Shared-key auth. Body { rows }. */
  glassbillerHook: "POST /api/hooks/glassbiller",
  listStores: "GET /api/admin/stores",
  createStore: "POST /api/admin/stores",
  updateStore: "PUT /api/admin/stores/:storeId",
  /** Browser crash reports. No session needed (the login screen can crash too). Capped and rate-limited. */
  clientErrors: "POST /api/client-errors",
} as const;

/** The ops keying queue: each job with the fields ops copies into GlassBiller, in screen order. */
export const packetFieldSchema = z.object({ label: z.string(), value: z.string() });
export const opsQueueResponseSchema = z.object({
  items: z.array(
    z.object({
      job: claimViewSchema,
      storeName: z.string().optional(),
      packet: z.array(packetFieldSchema),
    }),
  ),
});
export type OpsQueueItem = z.infer<typeof opsQueueResponseSchema>["items"][number];

/** Reports: one light row per claim in range. The browser groups by rep, dealership, ZIP and period. */
export const reportsQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
});
export const reportRowSchema = z.object({
  jobId: z.string(),
  jobNumber: z.string(),
  submittedAt: z.iso.datetime(),
  storeId: z.string(),
  storeName: z.string(),
  state: z.string().optional(),
  repId: z.string(),
  repName: z.string(),
  zip: z.string().optional(),
  claimStatus: z.enum(CLAIM_STATUSES),
  installedAt: z.string().optional(),
});
export type ReportRow = z.infer<typeof reportRowSchema>;
export const reportsResponseSchema = z.object({ rows: z.array(reportRowSchema) });

/**
 * What a GHL workflow posts back. Finds the claim by portal job number. `changedAt` is when the
 * change happened in GHL (the workflow sends the trigger time): a post older than the claim's last
 * change is a stale replay and changes nothing.
 */
export const ghlHookSchema = z.object({
  jobNumber: z.string().trim().regex(/^AGW-D-\d{6}$/),
  status: z.string().optional(),
  glassBillerJobNumber: z.string().optional(),
  gbStatus: z.string().optional(),
  gbLink: z.string().optional(),
  scheduleNote: z.string().optional(),
  scheduledFor: z.string().optional(),
  installedAt: z.string().optional(),
  filedBy: z.string().optional(),
  officeNotes: z.string().optional(),
  changedAt: z.string().optional(),
});
export type GhlHook = z.infer<typeof ghlHookSchema>;

/**
 * One GlassBiller job as the BigQuery sync sees it (`Glassbiller_Raw.jobs`). `status` is GlassBiller's own word
 * (workOrder, invoice, void, warranty...). Dates are calendar dates. "invoice" means the glass is in: 604 of 608
 * claims the office marked Installed were invoiced in GlassBiller (checked 23 Oct 2026); install_date alone is
 * only the planned day.
 */
export const glassBillerJobRowSchema = z.object({
  glassBillerJobNumber: gbJobNumber,
  status: z.string().trim().min(1).max(40),
  scheduledDate: z.iso.date().optional(),
  installDate: z.iso.date().optional(),
  invoiceDate: z.iso.date().optional(),
});
export type GlassBillerJobRow = z.infer<typeof glassBillerJobRowSchema>;
export const MAX_GLASSBILLER_HOOK_ROWS = 2000;
